import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { LogOut, Trash2, Plus, Mail, Phone, Loader2, Pencil } from "lucide-react";

function EnquiriesTab() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/enquiries").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => { await api.patch(`/enquiries/${id}`, { status }); load(); };
  const del = async (id) => { await api.delete(`/enquiries/${id}`); toast.success("Enquiry deleted"); load(); };

  if (items === null) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-emerald" /></div>;
  if (items.length === 0) return <p className="text-ink/50 py-20 text-center">No enquiries yet.</p>;

  return (
    <div className="space-y-4" data-testid="enquiries-list">
      {items.map((e) => (
        <div key={e.id} className="border border-emerald/10 p-6 bg-white flex flex-col md:flex-row md:items-center gap-4" data-testid={`enquiry-${e.id}`}>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <p className="font-serif text-xl text-emerald">{e.name}</p>
              <span className="text-[0.6rem] uppercase tracking-widest px-2 py-0.5 border border-gold/40 text-gold">{e.travel_type}</span>
              {e.package && <span className="text-xs text-ink/50">· {e.package}</span>}
            </div>
            <div className="flex flex-wrap gap-4 mt-2 text-sm text-ink/60">
              <a href={`mailto:${e.email}`} className="flex items-center gap-1 hover:text-emerald"><Mail className="w-3.5 h-3.5" /> {e.email}</a>
              <a href={`tel:${e.phone}`} className="flex items-center gap-1 hover:text-emerald"><Phone className="w-3.5 h-3.5" /> {e.phone}</a>
              {e.travelers && <span>{e.travelers}</span>}
              {e.travel_date && <span>{e.travel_date}</span>}
            </div>
            {e.message && <p className="text-ink/60 text-sm mt-2 italic">"{e.message}"</p>}
            <p className="text-ink/30 text-xs mt-2">{new Date(e.created_at).toLocaleString()}</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={e.status} onChange={(ev) => setStatus(e.id, ev.target.value)} data-testid={`enquiry-status-${e.id}`}
              className="text-xs border border-emerald/20 px-2 py-2 bg-cream focus:outline-none">
              <option value="new">New</option><option value="contacted">Contacted</option><option value="converted">Converted</option><option value="closed">Closed</option>
            </select>
            <button onClick={() => del(e.id)} className="w-9 h-9 border border-red-200 text-red-500 flex items-center justify-center hover:bg-red-50" data-testid={`delete-enquiry-${e.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

const EMPTY = { title: "", excerpt: "", content: "", cover_image: "", category: "Travel Guide", author: "Kerala Holidays Connect", published: true };

function BlogForm({ initial, onDone }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (initial?.id) await api.put(`/blog/${initial.id}`, form);
      else await api.post("/blog", form);
      toast.success("Post saved");
      onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const field = "w-full bg-transparent border-b border-ink/20 py-2.5 focus:outline-none focus:border-emerald text-sm";
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="blog-form">
      <input required placeholder="Title" value={form.title} onChange={set("title")} className={field} data-testid="blog-title" />
      <input required placeholder="Cover image URL" value={form.cover_image} onChange={set("cover_image")} className={field} data-testid="blog-cover" />
      <input placeholder="Category" value={form.category} onChange={set("category")} className={field} data-testid="blog-category" />
      <textarea required placeholder="Excerpt (short summary)" value={form.excerpt} onChange={set("excerpt")} className={field + " min-h-[70px]"} data-testid="blog-excerpt" />
      <textarea required placeholder="Content (use line breaks for paragraphs)" value={form.content} onChange={set("content")} className={field + " min-h-[160px]"} data-testid="blog-content" />
      <button type="submit" disabled={loading} className="w-full bg-emerald text-cream py-3 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-gold transition-colors flex items-center justify-center gap-2" data-testid="blog-save">
        {loading && <Loader2 className="w-4 h-4 animate-spin" />} Save Post
      </button>
    </form>
  );
}

function BlogTab() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/blog?all=true").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const done = () => { setOpen(false); setEditing(null); load(); };
  const del = async (id) => { await api.delete(`/blog/${id}`); toast.success("Post deleted"); load(); };

  return (
    <div data-testid="blog-admin">
      <div className="flex justify-end mb-6">
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <button className="bg-emerald text-cream px-5 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold hover:bg-gold transition-colors flex items-center gap-2" data-testid="new-post-btn"><Plus className="w-4 h-4" /> New Post</button>
          </DialogTrigger>
          <DialogContent className="max-w-lg bg-cream max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-serif text-3xl font-light text-emerald">{editing ? "Edit Post" : "New Post"}</DialogTitle></DialogHeader>
            <BlogForm initial={editing} onDone={done} />
          </DialogContent>
        </Dialog>
      </div>
      {items === null ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-emerald" /></div> : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="blog-list-admin">
          {items.map((p) => (
            <div key={p.id} className="border border-emerald/10 bg-white flex" data-testid={`blog-item-${p.slug}`}>
              <img src={p.cover_image} alt={p.title} className="w-28 object-cover" />
              <div className="p-4 flex-1">
                <span className="eyebrow text-gold text-[0.6rem]">{p.category}</span>
                <p className="font-serif text-lg text-emerald leading-tight mt-1">{p.title}</p>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => { setEditing(p); setOpen(true); }} className="text-xs flex items-center gap-1 text-emerald hover:text-gold" data-testid={`edit-blog-${p.slug}`}><Pencil className="w-3.5 h-3.5" /> Edit</button>
                  <button onClick={() => del(p.id)} className="text-xs flex items-center gap-1 text-red-500 hover:text-red-700" data-testid={`delete-blog-${p.slug}`}><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const out = () => { logout(); nav("/admin"); };

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="bg-emerald text-cream">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-10 py-6 flex items-center justify-between">
          <div>
            <p className="font-serif text-2xl font-light">Admin Dashboard</p>
            <p className="text-cream/60 text-xs">{user?.email}</p>
          </div>
          <button onClick={out} className="flex items-center gap-2 border border-cream/30 px-4 py-2 text-xs uppercase tracking-widest hover:bg-cream hover:text-emerald transition-colors" data-testid="logout-btn"><LogOut className="w-4 h-4" /> Logout</button>
        </div>
      </header>
      <main className="max-w-[1200px] mx-auto px-5 lg:px-10 py-10">
        <Tabs defaultValue="enquiries">
          <TabsList className="mb-8">
            <TabsTrigger value="enquiries" data-testid="tab-enquiries">Enquiries</TabsTrigger>
            <TabsTrigger value="blog" data-testid="tab-blog">Blog Posts</TabsTrigger>
          </TabsList>
          <TabsContent value="enquiries"><EnquiriesTab /></TabsContent>
          <TabsContent value="blog"><BlogTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
